import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { workspaceSummary } from '../../lib/workspace-summary'
import { RiAddLine, RiBuilding2Line } from '@remixicon/react'
import type { PartnerWorkspaceListItem } from 'shared'
import { Alert, Button, EmptyState, PageHeader, SectionSpinner } from '../../components/ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { IDENTITY_BADGE, monogram } from '../../lib/identity'
import { cx, focusRing } from '../../lib/utils'

/** The companies this partner created: who is in, who is waiting, nothing of what happens inside. */
export default function PartnerWorkspaces() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [workspaces, setWorkspaces] = useState<PartnerWorkspaceListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ data: PartnerWorkspaceListItem[] }>('/api/partner/workspaces')
      .then((response) => setWorkspaces(response.data))
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <>
      <PageHeader
        title={t('partner.title')}
        subtitle={t('partner.subtitle')}
        actions={
          <Button onClick={() => navigate('/partner/new')}>
            <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
            {t('partner.new')}
          </Button>
        }
      />

      {loading ? (
        <SectionSpinner />
      ) : error ? (
        <Alert>{error === 'error' ? t('errors.generic') : error}</Alert>
      ) : workspaces.length === 0 ? (
        <EmptyState
          icon={<RiBuilding2Line className="size-6" aria-hidden="true" />}
          title={t('partner.emptyTitle')}
          description={t('partner.emptyDescription')}
          action={<Button onClick={() => navigate('/partner/new')}>{t('partner.new')}</Button>}
        />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {workspaces.map((workspace) => {
            return (
              <li key={workspace.id} className="min-w-0">
                <Link
                  to={`/partner/${workspace.id}`}
                  className={cx(
                    'group relative flex h-full flex-col overflow-hidden rounded-xl bg-white p-5 ring-1 ring-gray-200 transition duration-150',
                    'hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-900/5 hover:ring-gray-300',
                    'dark:bg-gray-950 dark:ring-gray-800 dark:hover:ring-gray-700',
                    focusRing,
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className={cx(
                        'flex size-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold ring-1',
                        IDENTITY_BADGE,
                      )}
                    >
                      {monogram(workspace.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-display-sm text-gray-950 dark:text-white">
                        {workspace.name}
                      </h2>
                      <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                        {workspaceSummary(workspace, t) || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
                    <span>{t('workspaces.memberCount', { count: workspace.member_count })}</span>
                    <span>
                      {workspace.pending_invitation_count > 0
                        ? t('partner.pendingInvitations', {
                            count: workspace.pending_invitation_count,
                          })
                        : t('partner.noPendingInvitations')}
                    </span>
                  </div>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
